import { API } from "config";
import type { Task, TaskCreate } from "types/Task";
import request from "utilities/request";

const baseUrl = API.dbApi.url;

/**
 * Builds the POST body from only the persisted create columns. The backend
 * schema is `extra="forbid"`, and `TaskCreate` is a TS `Omit` type that does
 * not strip fields at runtime, so we enumerate the wire fields explicitly to
 * avoid sending a client-only field (e.g. `pollExhausted`) that would 422.
 */
function toCreateBody(task: TaskCreate): TaskCreate {
  return {
    threadId: task.threadId,
    kind: task.kind,
    serverUrl: task.serverUrl,
    groupName: task.groupName,
    statusTool: task.statusTool,
    oauthScopes: task.oauthScopes,
    jobId: task.jobId,
    correlationId: task.correlationId,
    ownerJobId: task.ownerJobId,
    toolName: task.toolName,
    displayName: task.displayName,
    status: task.status,
    statusReason: task.statusReason,
    statusRaw: task.statusRaw,
    statusCachedAt: task.statusCachedAt,
    resultLinks: task.resultLinks,
    archiveUrl: task.archiveUrl,
    fetchTaskId: task.fetchTaskId,
    pollInterval: task.pollInterval,
    dismissed: task.dismissed,
  };
}

/**
 * Creates a task row. Idempotent on (user_id, job_id): a duplicate returns the
 * existing row (200), not a 409. One exception: a body carrying `fetchTaskId`
 * attaches the tracker fields to the row the backend already inserted for that
 * task, which is how a fetch tracker is registered (ADR 059, "Registering it").
 * A 404 here means an unknown/unowned `threadId`
 * (register call sites already gate on a persisted chat, so a 404 is a genuine
 * error and is allowed to surface).
 */
export async function postTask(
  task: TaskCreate,
  getAccessToken: () => Promise<string>
): Promise<Task> {
  const accessToken = await getAccessToken();

  const url = `${baseUrl}/tasks`;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
  };
  const options = {
    method: "POST",
    headers,
    body: JSON.stringify(toCreateBody(task)),
  };

  const response = await request(url, options);
  return (await response.json()) as Task;
}
